import { useMemo, useState } from 'react';
import triangle from './img/triangle.png';
import triangleBas from './img/triangle-bas.png';
import logo from './img/logo-bleu.png';
import StartScreen from './StartScreen';
import ResultScreen from './ResultScreen';
import QuestionStep from './QuestionStep';
import { FormErrors } from './FormErrors';
import {
  getFirstQuestionId,
  getOrderedQuestionIds,
  getQuestionById,
  getRejectionReason,
  evaluateResources,
  buildEligibleResult,
  buildIneligibleResult,
} from '../engine/eligibility';

const PHASE = {
  START: 'start',
  QUESTIONS: 'questions',
  RESULT: 'result',
};

function createInitialAnswers() {
  return {
    usage_professionnel: undefined,
    vehicule_conditions: undefined,
    grand_rouleur: undefined,
    domicile_fiscal: undefined,
    age: undefined,
    revenu_activite: undefined,
    types_revenus: [],
    ifi: undefined,
    rfr: '',
    parts_fiscales: '',
  };
}

export default function Simulateur() {
  const questionIds = useMemo(() => getOrderedQuestionIds(), []);
  const [phase, setPhase] = useState(PHASE.START);
  const [currentQuestionId, setCurrentQuestionId] = useState(getFirstQuestionId());
  const [answers, setAnswers] = useState(createInitialAnswers);
  const [errors, setErrors] = useState([]);
  const [result, setResult] = useState(null);

  const currentQuestion = getQuestionById(currentQuestionId);
  const stepIndex = questionIds.indexOf(currentQuestionId);
  const progress =
    phase === PHASE.QUESTIONS && stepIndex >= 0
      ? ((stepIndex + 1) / questionIds.length) * 100
      : 0;

  const restart = () => {
    setPhase(PHASE.START);
    setCurrentQuestionId(getFirstQuestionId());
    setAnswers(createInitialAnswers());
    setErrors([]);
    setResult(null);
  };

  const goToIneligible = (questionId, extras = {}) => {
    setResult(buildIneligibleResult(getRejectionReason(questionId), extras));
    setPhase(PHASE.RESULT);
    setErrors([]);
  };

  const finishWithResources = () => {
    const evaluation = evaluateResources(answers.rfr, answers.parts_fiscales);
    if (evaluation.eligible) {
      setResult(buildEligibleResult(evaluation.rfrPerPart));
    } else {
      setResult(
        buildIneligibleResult(evaluation.reason, {
          rfrPerPart: evaluation.rfrPerPart,
        })
      );
    }
    setPhase(PHASE.RESULT);
    setErrors([]);
  };

  const validateCurrent = () => {
    const question = getQuestionById(currentQuestionId);
    const nextErrors = [];

    if (!question) {
      nextErrors.push('Question introuvable.');
      setErrors(nextErrors);
      return false;
    }

    if (question.type === 'radio') {
      if (answers[question.id] === undefined || answers[question.id] === null) {
        nextErrors.push('Veuillez sélectionner une réponse.');
      }
    }

    if (question.type === 'checkbox') {
      const selected = answers[question.id] || [];
      const min = question.min_selections ?? 1;
      if (selected.length < min) {
        nextErrors.push('Veuillez cocher au moins une case.');
      }
    }

    if (question.type === 'group') {
      question.fields.forEach((field) => {
        const raw = answers[field.id];
        const num = Number(raw);
        if (raw === '' || raw === null || raw === undefined || Number.isNaN(num)) {
          nextErrors.push(`Veuillez renseigner « ${field.label} ».`);
          return;
        }
        if (typeof field.min === 'number' && num < field.min) {
          nextErrors.push(`« ${field.label} » doit être au moins ${field.min}.`);
        }
      });
    }

    setErrors(nextErrors);
    return nextErrors.length === 0;
  };

  const handleNext = () => {
    if (!validateCurrent()) return;

    const question = getQuestionById(currentQuestionId);

    if (question.type === 'radio') {
      const selected = question.options.find((opt) => opt.value === answers[question.id]);
      if (!selected) return;

      if (selected.result === 'ineligible') {
        goToIneligible(question.id);
        return;
      }

      if (selected.next) {
        setCurrentQuestionId(selected.next);
        setErrors([]);
        return;
      }
    }

    if (question.type === 'checkbox') {
      if (!answers[question.id]?.length) {
        goToIneligible(question.id);
        return;
      }
      if (question.next === 'eligibility_rules') {
        finishWithResources();
        return;
      }
      if (question.next) {
        setCurrentQuestionId(question.next);
        setErrors([]);
      }
      return;
    }

    if (question.type === 'group') {
      if (question.next === 'eligibility_rules') {
        finishWithResources();
        return;
      }
      if (question.next) {
        setCurrentQuestionId(question.next);
        setErrors([]);
      }
    }
  };

  const onRadioChange = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setErrors([]);
  };

  const onCheckboxChange = (id, values) => {
    setAnswers((prev) => ({ ...prev, [id]: values }));
    setErrors([]);
  };

  const onFieldChange = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setErrors([]);
  };

  return (
    <div className="Simulateur">
      {phase === PHASE.START && <StartScreen onStart={() => setPhase(PHASE.QUESTIONS)} />}

      {phase === PHASE.QUESTIONS && currentQuestion && (
        <div className="step-container">
          <img src={triangle} alt="" className="desktop-only triangle-top" />
          <img src={triangleBas} alt="" className="mobile-only trianglebas" />


          <p className="step-counter">
            Question {stepIndex + 1} / {questionIds.length}
          </p>

          <div className="step-inner-container show">
            <QuestionStep
              question={currentQuestion}
              answers={answers}
              onRadioChange={onRadioChange}
              onCheckboxChange={onCheckboxChange}
              onFieldChange={onFieldChange}
            />
          </div>

          <FormErrors errors={errors} />

          <div className="button-container">
            <button type="button" className="btn btn-next" onClick={handleNext}>
              Suivant
            </button>
            <div className="restart-container">
              <button type="button" className="btn restart-btn" onClick={restart}>
                Recommencer
              </button>
            </div>
          </div>

          <img src={logo} alt="Logo" className="logo logo-corner" />
        </div>
      )}

      {phase === PHASE.RESULT && result && (
        <ResultScreen result={result} onRestart={restart} />
      )}
    </div>
  );
}
